import { useEffect, useState } from "react";
import { cn } from "../../../lib/utils";

/**
 * The card is two cells wide; every tile is one cell. Keep `CARD_WIDTH` in step
 * with the card's `sm:w-[480px]` in AuthShell.
 */
export const CARD_WIDTH = 480;
export const CELL = CARD_WIDTH / 2;

/** Cadence of the random walk. Several tiles update per tick, then ease out. */
const TICK_MS = 900;
const MOVE_MS = 1800;
const MIN_ACTIONS_PER_TICK = 2;
const MAX_ACTIONS_PER_TICK = 4;

/** The two fills a tile toggles between, both a shade off the paper page. */
type Fill = "tint" | "white";

/** A rectangle of cells (inclusive), in grid units from the card's top-left. */
export type Region = {
  colMin: number;
  colMax: number;
  rowMin: number;
  rowMax: number;
};

export type Tile = {
  region: Region;
  col: number;
  row: number;
  fill: Fill;
  visible: boolean;
};

/**
 * Each tile roams its own rectangle of cells around the card (which spans
 * columns 0 and 1). All coordinates are anchored to the card's top-left corner,
 * so the tiles always land on the page's grid lines whatever the form's height.
 * The rectangles are disjoint, so tiles never share a cell, and each is two
 * cells on a side so a tile can step in every cardinal direction.
 */
const REGIONS = {
  topFarLeft: { colMin: -3, colMax: -2, rowMin: -2, rowMax: -1 },
  topLeft: { colMin: -1, colMax: 0, rowMin: -2, rowMax: -1 },
  topRight: { colMin: 1, colMax: 2, rowMin: -2, rowMax: -1 },
  topFarRight: { colMin: 3, colMax: 4, rowMin: -2, rowMax: -1 },
  leftUpper: { colMin: -2, colMax: -1, rowMin: 0, rowMax: 1 },
  leftLower: { colMin: -2, colMax: -1, rowMin: 2, rowMax: 3 },
  bottomLeft: { colMin: -1, colMax: 0, rowMin: 4, rowMax: 5 },
  bottomRight: { colMin: 1, colMax: 2, rowMin: 4, rowMax: 5 },
  rightUpper: { colMin: 2, colMax: 3, rowMin: 0, rowMax: 1 },
  rightLower: { colMin: 2, colMax: 3, rowMin: 2, rowMax: 3 }
} as const satisfies Record<string, Region>;

const FILL_CLASS: Record<Fill, string> = {
  tint: "bg-tint",
  white: "bg-white"
};

const NEXT_FILL: Record<Fill, Fill> = {
  tint: "white",
  white: "tint"
};

/** One tile per region, resting so the first frame is already composed. */
const INITIAL_TILES: Tile[] = [
  {
    region: REGIONS.topFarLeft,
    col: -3,
    row: -2,
    fill: "white",
    visible: false
  },
  { region: REGIONS.topLeft, col: 0, row: -1, fill: "tint", visible: true },
  {
    region: REGIONS.topRight,
    col: 1,
    row: -1,
    fill: "white",
    visible: false
  },
  {
    region: REGIONS.topFarRight,
    col: 4,
    row: -1,
    fill: "tint",
    visible: false
  },
  {
    region: REGIONS.leftUpper,
    col: -1,
    row: 0,
    fill: "white",
    visible: true
  },
  { region: REGIONS.leftLower, col: -2, row: 3, fill: "tint", visible: false },
  {
    region: REGIONS.bottomLeft,
    col: 0,
    row: 4,
    fill: "white",
    visible: false
  },
  { region: REGIONS.bottomRight, col: 1, row: 5, fill: "tint", visible: true },
  { region: REGIONS.rightUpper, col: 2, row: 0, fill: "tint", visible: true },
  {
    region: REGIONS.rightLower,
    col: 3,
    row: 2,
    fill: "white",
    visible: false
  }
];

/** Card-relative position of a tile's cell, in pixels from the card's top-left. */
function slotStyle(tile: Tile): { top: number; left: number } {
  return { top: tile.row * CELL, left: tile.col * CELL };
}

function pick<T>(items: readonly [T, ...T[]]): T {
  return items[Math.floor(Math.random() * items.length)] ?? items[0];
}

/** Orthogonal neighbours: one cell east, west, south or north. */
const STEPS = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1]
] as const;

/** Step one cell in a cardinal direction, staying inside the tile's region. */
export function step(tile: Tile): Tile {
  const { region } = tile;
  const opts = STEPS.map(([dc, dr]) => ({
    col: tile.col + dc,
    row: tile.row + dr
  })).filter(
    (c) =>
      c.col >= region.colMin &&
      c.col <= region.colMax &&
      c.row >= region.rowMin &&
      c.row <= region.rowMax
  );
  const next = opts[Math.floor(Math.random() * opts.length)];
  return next ? { ...tile, col: next.col, row: next.row } : tile;
}

type Action = "move" | "visibility" | "fill";

/** Weighted toward visible cardinal movement, with occasional neutral flickers. */
const ACTIONS = [
  "move",
  "move",
  "move",
  "move",
  "move",
  "move",
  "fill",
  "fill",
  "fill",
  "visibility",
  "visibility"
] as const satisfies readonly Action[];

function randomInt(min: number, max: number): number {
  return min + Math.floor(Math.random() * (max - min + 1));
}

function runAction(tile: Tile, action: Action): Tile {
  switch (action) {
    case "move":
      return { ...step(tile), visible: true };
    case "visibility":
      return { ...tile, visible: !tile.visible };
    case "fill":
      return { ...tile, fill: NEXT_FILL[tile.fill] };
    default:
      return tile;
  }
}

/**
 * Cell-sized tiles that drift along the hairline grid around the sign-in card.
 * Each tick randomly picks a few different tiles and either steps them to a
 * neighbouring cell, fades them, or swaps between tint and white. Every
 * cell is anchored to the grid, so the tiles sit exactly on its lines.
 * Decorative only; sits under the grid lines and the card. Still under reduced
 * motion and below `sm`.
 */
export function AuthGridTiles() {
  const [tiles, setTiles] = useState<Tile[]>(INITIAL_TILES);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }

    const tick = () => {
      if (document.hidden) return;

      setTiles((prev) => {
        const actionCount = randomInt(
          MIN_ACTIONS_PER_TICK,
          MAX_ACTIONS_PER_TICK
        );
        const indexes = new Set<number>();
        while (indexes.size < actionCount) {
          indexes.add(Math.floor(Math.random() * prev.length));
        }

        return prev.map((tile, i) => {
          if (!indexes.has(i)) return tile;
          return runAction(tile, pick(ACTIONS));
        });
      });
    };

    const interval = window.setInterval(tick, TICK_MS);
    return () => window.clearInterval(interval);
  }, []);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-10 hidden sm:block"
    >
      {tiles.map((tile, index) => {
        const { top, left } = slotStyle(tile);
        return (
          <span
            key={index}
            className={cn(
              "absolute transition-[top,left,background-color,opacity] ease-in-out",
              FILL_CLASS[tile.fill]
            )}
            style={{
              top,
              left,
              width: CELL,
              height: CELL,
              opacity: tile.visible ? 1 : 0,
              transitionDuration: `${MOVE_MS}ms`
            }}
          />
        );
      })}
    </div>
  );
}
